import {globals} from '../../../globals';

const DEFAULT_TIMEOUT_MS = 8000;

function toPlainObject(value) {
  if (value === null || value === undefined) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
}

function buildWorkerSource() {
  return `
const toQueryString = (query) => {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.set(key, String(value));
  });
  return params.toString();
};

const createContext = ({token, schemaId, postgrestUrl}) => {
  const getJson = async (resource, options = {}) => {
    const query = toQueryString(options.query || {});
    const url = query ? \`\${postgrestUrl}\${resource}?\${query}\` : \`\${postgrestUrl}\${resource}\`;
    const response = await fetch(url, {
      headers: {
        Authorization: \`Bearer \${token}\`,
        'Accept-Profile': schemaId,
        Accept: 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(\`PostgREST request failed (\${response.status}) for \${resource}\`);
    }
    return await response.json();
  };
  return Object.freeze({
    schemaId,
    // Intentionally do not expose direct token usage helpers.
    postgrest: Object.freeze({
      getJson,
    }),
  });
};

self.onmessage = async (event) => {
  const {scriptSource, params, runtimeContext} = event.data || {};
  try {
    const moduleUrl = URL.createObjectURL(
      new Blob([scriptSource], {type: 'text/javascript'})
    );
    const moduleRef = await import(moduleUrl);
    URL.revokeObjectURL(moduleUrl);
    if (typeof moduleRef.buildReportData !== 'function') {
      throw new Error('Custom script must export buildReportData(params, context)');
    }
    const context = createContext(runtimeContext || {});
    const result = await moduleRef.buildReportData(params || {}, context);
    self.postMessage({ok: true, result: result || {}});
  } catch (error) {
    self.postMessage({
      ok: false,
      error: error?.message || 'Unknown custom report error',
    });
  }
};
`;
}

async function fetchScriptFromStorage(scriptId) {
  const response = await fetch(
    `${process.env.VUE_APP_STORAGE_URL}documents/${scriptId}/data`,
    {
      headers: {
        Authorization: `Bearer ${globals.$keycloak.token}`,
        'Accept-Profile': globals.$keycloak.tokenParsed.schema_id,
      },
    }
  );
  if (!response.ok) {
    throw new Error(`Unable to fetch custom report script (${response.status})`);
  }
  return await response.text();
}

export class CustomReportRunner {
  static async run({scriptId, params, timeoutMs = DEFAULT_TIMEOUT_MS}) {
    if (!scriptId) {
      throw new Error('Missing custom report script id');
    }
    const scriptSource = await fetchScriptFromStorage(scriptId);
    const workerSource = buildWorkerSource();
    const workerUrl = URL.createObjectURL(
      new Blob([workerSource], {type: 'text/javascript'})
    );
    const worker = new Worker(workerUrl);
    URL.revokeObjectURL(workerUrl);

    return await new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        worker.terminate();
        reject(new Error('Custom report script timeout'));
      }, timeoutMs);

      worker.onmessage = (event) => {
        clearTimeout(timer);
        worker.terminate();
        const payload = event.data || {};
        if (!payload.ok) {
          reject(new Error(payload.error || 'Custom report execution failed'));
          return;
        }
        resolve(payload.result || {});
      };

      worker.onerror = (error) => {
        clearTimeout(timer);
        worker.terminate();
        reject(new Error(error?.message || 'Custom report worker failure'));
      };

      worker.postMessage({
        scriptSource,
        params: toPlainObject(params),
        runtimeContext: {
          token: globals.$keycloak.token,
          schemaId: globals.$keycloak.tokenParsed.schema_id,
          postgrestUrl: process.env.VUE_APP_POSTGREST_URL,
        },
      });
    });
  }
}
